import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import logoImg from '../images/logo.png';

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 30);
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const scrollTo = (id) => {
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' }), 400);
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileOpen(false);
  };

  const isMobile = windowWidth <= 768;

  const navStyle = {
    position: 'fixed',
    top: 0, left: 0, right: 0,
    height: '88px',
    zIndex: 1000,
    display: 'flex',
    alignItems: 'center',
    transition: 'background 0.3s ease, box-shadow 0.3s ease',
    background: scrolled ? 'rgba(255,255,255,0.98)' : 'rgba(255,255,255,0.95)',
    backdropFilter: 'blur(14px)',
    WebkitBackdropFilter: 'blur(14px)',
    boxShadow: scrolled ? '0 2px 24px rgba(0,0,0,0.09)' : '0 1px 0 rgba(0,0,0,0.07)',
    borderBottom: '1px solid rgba(228,220,210,0.7)',
  };

  const innerStyle = {
    width: '100%',
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '0 clamp(1.5rem, 4vw, 4rem)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: '100%',
  };

  const linkStyle = {
    fontFamily: "'Jost', sans-serif",
    fontSize: '0.875rem',
    fontWeight: 500,
    letterSpacing: '0.13em',
    textTransform: 'uppercase',
    color: '#2C2C2C',
    cursor: 'pointer',
    padding: '6px 0',
    position: 'relative',
    textDecoration: 'none',
    transition: 'color 0.2s',
    background: 'none',
    border: 'none',
  };

  const ctaStyle = {
    fontFamily: "'Jost', sans-serif",
    fontSize: '0.82rem',
    fontWeight: 600,
    letterSpacing: '0.13em',
    textTransform: 'uppercase',
    color: '#fff',
    background: '#1E1E1E',
    padding: '0.75rem 1.8rem',
    borderRadius: '999px',
    cursor: 'pointer',
    border: 'none',
    whiteSpace: 'nowrap',
    transition: 'background 0.2s, transform 0.2s',
  };

  return (
    <>
      <nav style={navStyle}>
        <div style={innerStyle}>
          {/* Logo */}
          <div
            onClick={() => navigate('/')}
            style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 }}
          >
            <img
              src={logoImg}
              alt="Swiss N Scott"
              style={{ height: '90px', width: 'auto', objectFit: 'contain', display: 'block' }}
            />
          </div>

          {/* Desktop Nav Links */}
          {!isMobile && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '2.75rem' }}>
              {[
                { label: 'Home', action: () => navigate('/') },
                { label: 'Products', action: () => scrollTo('categories') },
                { label: 'About', action: () => scrollTo('about') },
                { label: 'Contact', action: () => scrollTo('contact') },
              ].map(({ label, action }) => (
                <button
                  key={label}
                  style={linkStyle}
                  onClick={action}
                  onMouseEnter={e => e.currentTarget.style.color = '#B8965A'}
                  onMouseLeave={e => e.currentTarget.style.color = '#2C2C2C'}
                >{label}</button>
              ))}
              <button
                style={ctaStyle}
                onClick={() => scrollTo('contact')}
                onMouseEnter={e => { e.currentTarget.style.background = '#B8965A'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = '#1E1E1E'; e.currentTarget.style.transform = 'translateY(0)'; }}
              >
                Get a Quote
              </button>
            </div>
          )}

          {/* Mobile Toggle */}
          {isMobile && (
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                display: 'flex', flexDirection: 'column', gap: '6px', padding: '8px',
              }}
              aria-label="Toggle menu"
            >
              <span style={{ display: 'block', width: '26px', height: '2px', background: '#1E1E1E', borderRadius: '2px', transition: 'all 0.25s', transform: mobileOpen ? 'rotate(45deg) translate(5px, 6px)' : 'none' }} />
              <span style={{ display: 'block', width: '26px', height: '2px', background: '#1E1E1E', borderRadius: '2px', transition: 'all 0.25s', opacity: mobileOpen ? 0 : 1 }} />
              <span style={{ display: 'block', width: '26px', height: '2px', background: '#1E1E1E', borderRadius: '2px', transition: 'all 0.25s', transform: mobileOpen ? 'rotate(-45deg) translate(5px, -6px)' : 'none' }} />
            </button>
          )}
        </div>
      </nav>

      {/* Mobile Dropdown */}
      {isMobile && mobileOpen && (
        <div style={{
          position: 'fixed', top: '88px', left: 0, right: 0,
          background: '#fff', boxShadow: '0 8px 32px rgba(0,0,0,0.10)',
          borderBottom: '2px solid #f2f2f2', zIndex: 999,
          padding: '1.25rem 1.75rem 1.75rem',
          display: 'flex', flexDirection: 'column', gap: '0',
        }}>
          {[
            { label: 'Home', action: () => navigate('/') },
            { label: 'Categories', action: () => scrollTo('categories') },
            { label: 'About', action: () => scrollTo('about') },
            { label: 'Contact', action: () => scrollTo('contact') },
          ].map(({ label, action }) => (
            <button key={label} onClick={action} style={{
              fontFamily: "'Jost', sans-serif", fontSize: '0.95rem', fontWeight: 500,
              letterSpacing: '0.1em', textTransform: 'uppercase',
              color: '#404040', background: 'none', border: 'none',
              borderBottom: '1px solid #f2f2f2', padding: '1rem 0', textAlign: 'left', cursor: 'pointer',
            }}>
              {label}
            </button>
          ))}
          <button onClick={() => scrollTo('contact')} style={{
            marginTop: '1.25rem', fontFamily: "'Jost', sans-serif",
            fontSize: '0.85rem', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase',
            color: '#fff', background: '#1E1E1E', border: 'none', borderRadius: '999px',
            padding: '0.9rem', cursor: 'pointer',
          }}>
            Get a Quote
          </button>
        </div>
      )}
    </>
  );
};

export default Navbar;